import React, { useState } from 'react';
import { X, Search, Sparkles, Compass } from 'lucide-react';
import { FORMULA_VARIABLES } from '../data/formulas';
import { findFormulaRoute } from '../lib/formulaSolver';
import { FormulaRouteResult } from '../types';
import ReactMarkdownComponent from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';

interface FormulaSolverDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFormula?: (latex: string) => void;
}

export const FormulaSolverDrawer: React.FC<FormulaSolverDrawerProps> = ({
  isOpen,
  onClose,
  onApplyFormula,
}) => {
  const [selectedKnowns, setSelectedKnowns] = useState<string[]>([]);
  const [selectedTarget, setSelectedTarget] = useState<string>('');
  const [routeResult, setRouteResult] = useState<FormulaRouteResult | null>(null);

  if (!isOpen) return null;

  const toggleKnown = (varId: string) => {
    setSelectedKnowns((prev) =>
      prev.includes(varId) ? prev.filter((id) => id !== varId) : [...prev, varId]
    );
  };

  const handleSolve = () => {
    if (!selectedTarget) return;
    const res = findFormulaRoute(selectedKnowns, selectedTarget);
    setRouteResult(res);
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-md bg-paper-100 dark:bg-study-panel h-full shadow-2xl border-l border-paper-300 dark:border-study-line flex flex-col p-5 overflow-y-auto">
        <div className="flex items-center justify-between border-b border-paper-200 pb-3 dark:border-study-line">
          <div className="flex items-center gap-2">
            <Compass className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
            <h3 className="font-serif text-sm font-bold text-zinc-900 dark:text-zinc-100">
              Grafo de Fórmulas (BFS Solver)
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <p className="mt-2 text-[11px] text-zinc-500 dark:text-zinc-400">
          Selecione as variáveis conhecidas do problema e a incógnita alvo. O motor encontra a menor rota por equações.
        </p>

        {/* Knowns selector */}
        <div className="mt-4">
          <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            Grandezas Conhecidas (Inputs):
          </label>
          <div className="mt-1.5 flex flex-wrap gap-1.5 max-h-36 overflow-y-auto p-1 rounded border border-paper-200 bg-paper-50 dark:border-study-line dark:bg-study-card">
            {FORMULA_VARIABLES.map((v) => {
              const isSelected = selectedKnowns.includes(v.id);
              return (
                <button
                  key={v.id}
                  type="button"
                  onClick={() => toggleKnown(v.id)}
                  className={`px-2 py-0.5 rounded text-[11px] font-mono border transition ${
                    isSelected
                      ? 'bg-indigo-600 border-indigo-600 text-white font-bold'
                      : 'border-paper-300 bg-paper-100 text-zinc-700 hover:bg-paper-200 dark:border-study-line dark:bg-study-panel dark:text-zinc-300'
                  }`}
                >
                  {v.symbol} ({v.name})
                </button>
              );
            })}
          </div>
        </div>

        {/* Target selector */}
        <div className="mt-4">
          <label className="text-[11px] font-bold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">
            Incógnita Alvo (Output):
          </label>
          <select
            value={selectedTarget}
            onChange={(e) => setSelectedTarget(e.target.value)}
            className="mt-1 w-full rounded border border-paper-300 bg-paper-50 p-2 text-xs font-mono text-zinc-900 focus:outline-none focus:border-indigo-500 dark:border-study-line dark:bg-study-card dark:text-zinc-100"
          >
            <option value="">Selecione a incógnita...</option>
            {FORMULA_VARIABLES.map((v) => (
              <option key={v.id} value={v.id}>
                {v.symbol} — {v.name} ({v.unit})
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={handleSolve}
          disabled={!selectedTarget}
          className="mt-4 flex items-center justify-center gap-1.5 rounded bg-zinc-900 py-2 text-xs font-semibold uppercase tracking-wider text-white hover:bg-zinc-800 disabled:opacity-50 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white"
        >
          <Search className="h-3.5 w-3.5" />
          Executar BFS de Fórmulas
        </button>

        {/* Results */}
        {routeResult && (
          <div className="mt-5 rounded border border-paper-300 bg-paper-50 p-3.5 dark:border-study-line dark:bg-study-card">
            <div className="flex items-center gap-1.5 font-mono text-xs font-bold text-zinc-800 dark:text-zinc-200 mb-2">
              <Sparkles className="h-3.5 w-3.5 text-amber-500" />
              {routeResult.found
                ? `Rota Mínima Encontrada (${routeResult.steps.length} passo${
                    routeResult.steps.length > 1 ? 's' : ''
                  })`
                : 'Nenhuma rota direta encontrada com os dados atuais'}
            </div>

            {routeResult.found && (
              <div className="space-y-3 mt-2">
                {routeResult.steps.map((step, idx) => (
                  <div
                    key={idx}
                    className="rounded border border-paper-200 bg-paper-100 p-2.5 dark:border-study-line dark:bg-study-panel"
                  >
                    <div className="flex items-center justify-between text-[11px] text-zinc-500 dark:text-zinc-400 mb-1">
                      <span className="font-bold text-zinc-800 dark:text-zinc-200">
                        {step.equation.name}
                      </span>
                      <span className="font-mono text-[10px]">Passo {idx + 1}</span>
                    </div>

                    <div className="font-mono text-xs text-indigo-700 dark:text-indigo-400 py-1">
                      <ReactMarkdownComponent remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                        {`$${step.rearrangedLatex || step.equation.latex}$`}
                      </ReactMarkdownComponent>
                    </div>

                    <p className="text-[10px] text-zinc-500 dark:text-zinc-400 mt-1">
                      {step.equation.description}
                    </p>

                    {step.descriptorCode && (
                      <div className="mt-1 text-[9px] font-mono text-indigo-600 dark:text-indigo-400">
                        Descritor: {step.descriptorCode}
                      </div>
                    )}

                    {onApplyFormula && (
                      <button
                        type="button"
                        onClick={() => {
                          onApplyFormula(step.equation.latex);
                          onClose();
                        }}
                        className="mt-2 text-[10px] uppercase font-bold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
                      >
                        + Usar Esta Fórmula-Ponte na Triagem
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
